import { ReactNode } from 'react';
import { UserRetailAccount, RetailAccountContent } from '@shared/types';
import { formatFullDateTime, formatRelativeTime } from '../../utils/dateFormat';
import { filterOpenPositions } from './portfolioUtils';
import { DataCard } from '@/components/nq/DataCard';

interface PortfolioRebalancingPanelProps {
  account: UserRetailAccount;
  content?: RetailAccountContent;
  isUnfunded?: boolean;
}

const formatTrackRecord = (startIso?: string) => {
  if (!startIso) return '—';
  const start = new Date(startIso);
  const now = new Date();
  const totalDays = Math.floor((now.getTime() - start.getTime()) / 86_400_000);
  if (totalDays < 1) return 'Today';

  let months =
    (now.getFullYear() - start.getFullYear()) * 12 +
    (now.getMonth() - start.getMonth());
  let days = now.getDate() - start.getDate();
  if (days < 0) {
    months -= 1;
    days += new Date(now.getFullYear(), now.getMonth(), 0).getDate();
  }

  if (months <= 0) return `${days} day${days === 1 ? '' : 's'}`;
  const monthsPart = `${months} month${months === 1 ? '' : 's'}`;
  if (days <= 0) return monthsPart;
  return `${monthsPart} and ${days} day${days === 1 ? '' : 's'}`;
};

const pct = (num?: number, den?: number) =>
  num !== undefined && den && den !== 0
    ? `${((num / den) * 100).toFixed(1)}%`
    : '—';

const PortfolioRebalancingPanel = ({
  account,
  content,
  isUnfunded = false,
}: PortfolioRebalancingPanelProps) => {
  const tradingSinceIso =
    content?.tradingStartedAt ?? content?.trackRecordStartedAt ?? undefined;

  const rows: [string, ReactNode, string | undefined][] = [
    [
      'Last rebalanced',
      formatRelativeTime(account.lastRebalancedAt ?? undefined),
      account.lastRebalancedAt ? formatFullDateTime(account.lastRebalancedAt) : undefined,
    ],
    [
      content?.tradingStartedAt ? 'Trading since' : 'Track record',
      formatTrackRecord(tradingSinceIso),
      tradingSinceIso ? formatFullDateTime(tradingSinceIso) : undefined,
    ],
    ['Positions', filterOpenPositions(content?.positions ?? []).length, undefined],
    [
      'Cash / Invested',
      `${pct(content?.cash, content?.totalValue)} / ${pct(content?.stockValue, content?.totalValue)}`,
      undefined,
    ],
  ];

  return (
    <DataCard title="Rebalancing">
      <dl className="m-0 flex flex-col gap-3">
        {rows.map(([label, value, title], i) => (
          <div
            key={label}
            className={`flex justify-between gap-3 ${
              i < rows.length - 1
                ? 'border-b border-[color:rgba(255,255,255,0.06)] pb-2.5'
                : ''
            }`}
          >
            <dt className="text-xs uppercase tracking-[0.04em] text-muted-foreground">
              {label}
            </dt>
            <dd className="m-0 text-right text-sm font-semibold" title={title}>
              {value}
            </dd>
          </div>
        ))}
      </dl>
      <p className="mt-3.5 text-xs leading-relaxed text-muted-foreground">
        {isUnfunded
          ? 'Rebalancing requires a funded account. Deposit funds on Alpaca to get started.'
          : 'Target weights and trade history will appear when rebalancing APIs are connected.'}
      </p>
    </DataCard>
  );
};

export default PortfolioRebalancingPanel;
